import { useState, useEffect, useRef } from 'react';
import { Search, X, TrendingUp } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const SUGGESTIONS_NL = [
  'Nike Air Max',
  'Samsung TV',
  'Winterjas dames',
  'Koptelefoon',
  'Sneakers heren',
  'Gouden ketting',
  'Laptop',
  'Parfum',
];

const SUGGESTIONS_EN = [
  'Nike Air Max',
  'Samsung TV',
  'Winter jacket',
  'Headphones',
  'Sneakers',
  'Gold necklace',
  'Laptop',
  'Perfume',
];

const TRENDING_NL = [
  { label: 'Sneakers', query: 'sneakers' },
  { label: 'Laptops', query: 'laptop' },
  { label: 'Jassen', query: 'jas' },
  { label: 'Horloges', query: 'horloge' },
  { label: 'Koptelefoons', query: 'koptelefoon' },
];

const TRENDING_EN = [
  { label: 'Sneakers', query: 'sneakers' },
  { label: 'Laptops', query: 'laptop' },
  { label: 'Jackets', query: 'jacket' },
  { label: 'Watches', query: 'watch' },
  { label: 'Headphones', query: 'headphone' },
];

export default function SearchBar() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const [query, setQuery] = useState(searchParams.get('search') || '');
  const [isFocused, setIsFocused] = useState(false);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [placeholderText, setPlaceholderText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);

  const suggestions = language === 'nl' ? SUGGESTIONS_NL : SUGGESTIONS_EN;
  const trending = language === 'nl' ? TRENDING_NL : TRENDING_EN;
  const searchLabel = language === 'nl' ? 'Wat zoek je?' : 'What are you looking for?';
  const trendingLabel = language === 'nl' ? 'Populair' : 'Trending';

  // Animated placeholder typing effect
  useEffect(() => {
    if (query || isFocused) return;

    const currentSuggestion = suggestions[placeholderIndex];
    let charIndex = 0;
    let timeout: ReturnType<typeof setTimeout>;

    if (isTyping) {
      const typeChar = () => {
        if (charIndex <= currentSuggestion.length) {
          setPlaceholderText(currentSuggestion.slice(0, charIndex));
          charIndex++;
          timeout = setTimeout(typeChar, 60 + Math.random() * 40);
        } else {
          // Pause at full text, then start erasing
          timeout = setTimeout(() => setIsTyping(false), 2000);
        }
      };
      typeChar();
    } else {
      charIndex = currentSuggestion.length;
      const eraseChar = () => {
        if (charIndex >= 0) {
          setPlaceholderText(currentSuggestion.slice(0, charIndex));
          charIndex--;
          timeout = setTimeout(eraseChar, 30);
        } else {
          // Move to next suggestion
          setPlaceholderIndex((prev) => (prev + 1) % suggestions.length);
          setIsTyping(true);
        }
      };
      eraseChar();
    }

    return () => clearTimeout(timeout);
  }, [placeholderIndex, isTyping, query, isFocused, suggestions]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/?search=${encodeURIComponent(query.trim())}`);
      inputRef.current?.blur();
    }
  };

  const handleTrendingClick = (searchQuery: string) => {
    setQuery(searchQuery);
    navigate(`/?search=${encodeURIComponent(searchQuery)}`);
  };

  const clearSearch = () => {
    setQuery('');
    inputRef.current?.focus();
  };

  return (
    <div className="relative border-b border-primary/10">
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gradient-to-b from-primary/8 via-primary/4 to-transparent" />

      <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-5">
        {/* Search input */}
        <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
          <div
            className={`relative flex items-center rounded-2xl transition-all duration-300 ${
              isFocused
                ? 'bg-card/90 shadow-lg shadow-primary/10 ring-2 ring-primary/30 scale-[1.02]'
                : 'bg-card/60 shadow-md hover:bg-card/80 hover:shadow-lg'
            }`}
          >
            <Search className={`absolute left-4 w-5 h-5 transition-colors duration-200 ${
              isFocused ? 'text-primary' : 'text-muted-foreground'
            }`} />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder={query ? '' : `${searchLabel}  ${placeholderText}${!query && !isFocused ? '|' : ''}`}
              className="w-full pl-12 pr-12 py-3.5 md:py-4 bg-transparent text-foreground text-base md:text-lg placeholder:text-muted-foreground/60 focus:outline-none rounded-2xl"
            />
            {query && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-14 p-1.5 rounded-full hover:bg-muted/50 transition-colors"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            )}
            <button
              type="submit"
              className={`absolute right-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${
                query.trim()
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm'
                  : 'bg-muted/50 text-muted-foreground cursor-default'
              }`}
            >
              <Search className="w-4 h-4 md:hidden" />
              <span className="hidden md:inline">{language === 'nl' ? 'Zoeken' : 'Search'}</span>
            </button>
          </div>
        </form>

        {/* Trending tags */}
        <div className="flex items-center justify-center gap-2 mt-3 flex-wrap">
          <span className="flex items-center gap-1 text-xs text-muted-foreground/70">
            <TrendingUp className="w-3 h-3" />
            {trendingLabel}:
          </span>
          {trending.map((item) => (
            <button
              key={item.query}
              onClick={() => handleTrendingClick(item.query)}
              className="px-3 py-1 rounded-full text-xs font-medium bg-card/50 border border-primary/10 text-muted-foreground hover:text-foreground hover:border-primary/30 hover:bg-card/80 transition-all"
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
